<template>
  <div class="select-location-btn-wrapper">
    <button
      @click="handleClick"
      data-testid="button-location-filter"
      id="OffersLocationFilterButton"
    >
      <i
        class="icon-flag-map"
        :class="[mainIconSize, { 'icon-focus': isInputFocus }]"
        data-testid="icon-location-input"
      />
      <input
        v-if="!isDevice('mobile-l')"
        ref="inputLocation"
        :placeholder="translate('offLocation')"
        class="input-location-filter"
        type="text"
        @focusin="handleFocusIn"
        @focusout="handleFocusOut"
        v-model="location"
        data-testid="location-input"
      />
      <span data-testid="mobile-label-text" v-else class="text-mobile">{{ getMobileText }}</span>
      <button
        v-show="location && !isDevice('mobile-l')"
        id="clear-location-filter"
        data-testid="clear-location-btn"
        class="btn-clear-location"
        aria-label="Reset location"
        @click="handleClearLocation"
      >
        <i class="icon-cross xxs" />
      </button>
    </button>
    <PortalTarget name="select-location-btn-wrapper" />
    <Portal :to="getPortalTarget()">
      <MultiOffersLocationModal
        :is-open="isOpen"
        :locations="locations"
        :location="location"
        @toggle="toggle"
        :is-full-screen="isDevice('mobile-l')"
        @locationSelected="handleLocationSelected"
        @clearFilter="handleClearLocation"
      />
    </Portal>
  </div>
</template>

<script>
import { media, getQueryParam, hasQueryParam } from '@/infrastructure/Utils'
import MultiOffersLocationModal from '@/components/multi-offers/Filters/MultiOffersLocationModal'
import { translate } from 'rhg-vue-common'
import { createNamespacedHelpers } from 'vuex'
const { mapGetters, mapMutations } = createNamespacedHelpers('offersDestinations')
export default {
  name: 'MultiOffersLocationFilter',
  components: { MultiOffersLocationModal },
  props: {
    mainIconSize: {
      type: String,
      required: false,
      default: 'xs'
    },
    locations: {
      type: Array,
      required: true
    }
  },
  beforeMount() {
    this.handleLocationFilterValueFromUrl()
  },
  mounted() {
    this.handleCloseModal()
    this.$root.$on('updateFilter', ({ code, type }) => {
      this.handleLocationSelected({ code, type })
    })
    this.$root.$on('search-text', (newValue) => {
      this.location = newValue
    })
  },
  watch: {
    filteredDestinations(newValue) {
      if (newValue.length !== 0) {
        this.location = newValue[0].name
        this.getFormattedLocations.forEach(location => {
          if (location.name === newValue[0].name) {
            global.searchBarVue.$emit('location', location.destinationFormatted)
          } else {
            if (Array.isArray(location.cities)) {
              location.cities.forEach(city => {
                if (city?.name === newValue[0].name) {
                  global.searchBarVue.$emit('location', location.destinationFormatted)
                }
              })
            }
          }
        })
      } else if (newValue.length === 0 && this.selectedCode) {
        this.handleNoPreviousResults()
      } else {
        this.location = ''
      }
    },
    location(newValue) {
      if (newValue === '') {
        this.updateFilterApply({ type: '', code: '' })
        global.searchBarVue.$emit('location', '')
      }

      for (const location of this.getFormattedLocations) {
        if (location?.destinationFormatted.toLowerCase() === newValue?.toLowerCase()) {
          this.updateFilterApply({ type: location.type, code: location.code })
        }
      }
    }
  },
  computed: {
    ...mapGetters(['filteredDestinations', 'selectedCode', 'applyFilters', 'getFormattedLocations', 'allLocations']),
    getMobileText() {
      return this.location ? this.location : translate('offLocation')
    }
  },
  data() {
    return {
      isInputFocus: false,
      isOpen: false,
      location: ''
    }
  },
  methods: {
    ...mapMutations(['updateFilterApply']),
    toggle(isOpen) {
      this.isOpen = isOpen
      if (this.isOpen) {
        document.body.setAttribute('style', 'overflow: hidden;')
      } else {
        document.body.removeAttribute('style', 'overflow: hidden;')
      }
    },
    handleClearLocation() {
      this.updateFilterApply({ type: '', code: '' })
    },
    handleCloseModal() {
      window.addEventListener('click', e => {
        const buttonFilters = document.getElementById('OffersLocationFilterButton')
        const offersLocationModal = document.getElementById('offers-location-modal')
        if (offersLocationModal) {
          this.isOpen = offersLocationModal.contains(e.target)
        } else {
          this.isOpen = buttonFilters.contains(e.target)
        }
      })
    },
    handleLocationSelected(location) {
      this.updateFilterApply({ type: location.type, code: location.code })
      this.$emit('selectedLocation', location)
      global.searchBarVue.$emit('location', location.destinationFormatted)
    },
    handleClick() {
      if (!this.isInputFocus && !this.isDevice('mobile-l')) {
        this.$refs.inputLocation.focus()
      }
    },
    handleFocusIn() {
      this.isInputFocus = true
    },
    handleFocusOut() {
      this.isInputFocus = false
    },
    isDevice(type) {
      const clientWidth = window.innerWidth
      return clientWidth <= media(type)
    },
    getPortalTarget() {
      const clientWidth = window.screen.availWidth
      return clientWidth > media('mobile-l') ? 'select-location-btn-wrapper' : 'modal-root-location'
    },
    handleNoPreviousResults() {
      const [{ code, name }] = this.filteredDestinations
      if (code === this.selectedCode) {
        this.location = name
      }
    },
    handleLocationFilterValueFromUrl() {
      let code = ''
      const isCityCode = hasQueryParam('cityCode')
      const isCountryCode = hasQueryParam('countryCode')
      if (!isCityCode && !isCountryCode) {
        return
      } else if (isCityCode) {
        code = getQueryParam('cityCode')
      } else if (isCountryCode) {
        code = getQueryParam('countryCode')
      }

      for (const location of this.allLocations) {
        if (location.code !== code) {
          continue
        }

        this.location = location.name
      }
    }
  }
}
</script>

<style scoped lang="scss">
.text-mobile {
  color: color('dark-grey');
}
.select-location-btn-wrapper {
  position: relative;
  button {
    box-shadow: none;
    display: flex;
    align-items: center;
    gap: 1rem;
    border: 1px solid color('grey-cool-two');
    background-color: white;
    padding: 0.75rem 1rem;
    border-radius: 25px;
  }
  .input-location-filter {
    padding: 0.5rem 0;
    border: none;
    background-image: none;
    background-color: transparent;
    -webkit-box-shadow: none;
    -moz-box-shadow: none;
    box-shadow: none;
    caret-color: color('primary');
    width: 100%;
  }
  .icon-flag-map {
    color: color('dark-grey');
  }
  .icon-focus {
    color: color('primary');
  }
  input:focus {
    outline: none;
  }
  .btn-clear-location {
    position: absolute;
    right: 1rem;
    border: 0;
    i {
      color: color('dark-grey');
    }
  }
}

@media (max-width: media('mobile-l')) {
  .select-location-btn-wrapper {
    width: 100%;
    margin-top: 2rem;
    button {
      width: 100%;
    }
  }
}
</style>
